import React, { useEffect, useState } from "react"
import { Button, Col, Form, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import Select from "react-select"
import GlobalHelper from "../../../common/GlobalHelper"
import ConfirmButton from "../../../common/button/confirmbutton.component"
import Icon from "../../../common/icon/icon.component"
import TaxSelect from "../../../common/pricing/taxselect.component"
import { ContactActions } from "../../../state/contact/actions/actions"
import { ContactForm, SupplierFormProps, getFormFromContact } from "../../../state/contact/models"
import { setDuplicatedContactsByEmail } from "../../../state/contact/reducers"
import { Selector } from "../../../state/contact/selectors/selectors"
import IntlProps from "../../../state/helpers/IntlProps"
import { CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES, EDIT_STOCK_LOCATION_PAGE_MESSAGES } from "../../../utils/inventory"
import AddressFormFieldsComponent from "../../components/AddressFormFieldsComponent"
import { CREATE_NEW_SUPPLIER_MESSAGES } from "./listing/messages"
import { Guard, Permissions } from "../../../role/Guard"

const CreateSupplierForm = (props: SupplierFormProps & IntlProps) => {
    const dispatch = useDispatch()
    const duplicatedContactsByEmail = useSelector(Selector.geDuplicateContactsByEmail)
    const duplicatedContactsByPhoneNumber = useSelector(Selector.geDuplicateContactsByPhoneNumber)
    const [showAdvancedForm, setShowAdvancedForm] = useState(false)
    const [form, setForm] = useState<ContactForm>(getFormFromContact(props.contact, props.addressOnly)) 
    const [errors, setErrors] = useState({
        contactEmail: "",
        contactName: "",
        contactPrimaryPhoneNumber: "",
        contactPhoneNumberMobile:"",
    })
        
    const setFormField = (field: string, value: string) => {
        setForm(prevForm => ({
            ...prevForm,
            [field]: value
        }))


        if (errors[field]) {
            setErrors({
                ...errors,
                [field]: ""
            })
        }
    }

    useEffect(() => {
        if (checkDuplicateEmailList()) {
            dispatch(ContactActions.checkDuplicatedEmail({ value:form.emailKeyword,contactId:props.contact?.id }))
        } else {
            dispatch(setDuplicatedContactsByEmail([]))
        }
    }, [form.emailKeyword])


    useEffect(() => {
        if (form.contactPrimaryPhoneNumber && validPhoneNumber()) {
            dispatch(ContactActions.checkDuplicatedPhoneNumber({
                value: GlobalHelper.formatPhoneNumberDigitOnly(form.contactPrimaryPhoneNumber),
                contactId: props.contact?.id
            }))
    
        } else {
            dispatch(setDuplicatedContactsByEmail([]))
        }
    }, [form.phoneNumberKeywordCheckDup])

    const checkDuplicateEmailList=()=> {
        let isValid = true
        form.emailKeyword.filter(element => {
            if (!GlobalHelper.validateEmail(element)) {
                isValid = false
            }
        })
        return isValid
    }

    const getDuplicatedFormatted = (duplicatedResult) => {
        if (duplicatedResult.length == 0 || !duplicatedResult) {
            return <></>
        }

        return <div className="alert alert-warning" role="alert">
            {props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["emailKeyword"])}
            &nbsp;
            {duplicatedResult?.map(element =>{
                return <Link key={element.id} to={`/contacts/${element.id}/details`} className="mr-1">{element.name} </Link>
            }).reduce((prev, curr) => [prev, ", ", curr])}
        </div>
    }

    const findFormErrors = () => {
        const newErrors = {
            contactEmail: "",
            contactName: "",
            contactPrimaryPhoneNumber: "",
            contactPhoneNumberMobile:"",
        }
      
        if (!form.contactName) {
            newErrors["contactName"]=props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["nameMissingError"])
        }
        
        if (form.contactPrimaryPhoneNumber && !validPhoneNumber()) {
            newErrors["contactPrimaryPhoneNumber"]=props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["phone_format_error"])
        }

        if (form.contactEmail) {
            const re = new RegExp(GlobalHelper.getEmailValidationRegexString())
            if (!re.test(form.contactEmail)) {
                newErrors["contactEmail"]=props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["email_format_error"])
            }
        }

        return newErrors
    }

    const deleteContact = () => {
        dispatch(ContactActions.deleteContact(props.contact?.id))
    }
    
    const handleSave = (event) => {
        event.preventDefault()
        event.stopPropagation()
        const newErrors = findFormErrors()
        setErrors({
            contactEmail: newErrors.contactEmail,
            contactName: newErrors.contactName,
            contactPrimaryPhoneNumber: newErrors.contactPrimaryPhoneNumber,
            contactPhoneNumberMobile:newErrors.contactPhoneNumberMobile,
        })
        
        const hasErrors = Object.values(newErrors).some(errorMsg => errorMsg !== "")
        if (!hasErrors) {
            saveCustomer()
        }
    }

    const handleEmailChange = (event) => {
        const target = event.target.value
        setErrors(prevError => ({
            ...prevError,
            contactEmail: ""
        }))
        
        setForm(prevForm => ({
            ...prevForm,
            contactEmail:target,
            emailKeyword: target.split(","),
        }))
    }

    const validPhoneNumber=()=> {
        const formattedPhoneNumber = GlobalHelper.formatPhoneNumberDigitOnly(form.contactPrimaryPhoneNumber)
        return formattedPhoneNumber.length >= 10
    }

    const openAdvanced=(event)=> {
        event.preventDefault()
        setShowAdvancedForm(!showAdvancedForm)
    }

    const handleAddressChange = (change) => {
        setForm(prevForm => ({
            ...prevForm,
            address: {
                ...prevForm.address,
                ...change,
            },
        }))
    }

    const getRedirectLink = () => {
        if (props.contactId) {
            return `/suppliers/${props.contactId}/details`
        }
        return "/suppliers/listing"
    }

    const saveCustomer=()=> {
        const payloadPhoneNumbers:{type:string,phoneNumber:string,extension:string}[] = []
        if (form.contactPrimaryPhoneNumber) {
            payloadPhoneNumbers.push({
                type: "primary",
                phoneNumber: form.contactPrimaryPhoneNumber,
                extension: form.contactPrimaryPhoneNumberExtension

            })
        }
        
        if (form.contactPhoneNumberMobile) {
            payloadPhoneNumbers.push({
                type: "mobile",
                phoneNumber: form.contactPhoneNumberMobile,
                extension: ""
            })
        }

        const payloadSupplier = {
            name: form.contactName,
            email: form.contactEmail.trim(),
            phoneNumbers: payloadPhoneNumbers,
            language: form.contactLanguage,
            notes: form.contactNotes,
            labelIds: form.labelIds,
            taxId: form.taxId,
            taxNumber: form.taxNumber,
            address: {
                ...form.address,
                isDefault: true,
                addressType: "billing",
                firstName: form.contactName,
            },
        }
        
        dispatch(ContactActions.upsertContact({
            payloadSupplier: payloadSupplier,
            companyIdentifier: "",
            contactId: props.contact?.id || "",
            contactType:"suppliers",
        }))
    }

    return (
        <>
            <Form noValidate onSubmit={handleSave} className="ui-has-modules" >
                <div className="form-group">
                    <Col className="ui-module">
                        <Form.Group >
                            <Form.Label htmlFor="name"> 
                                {props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["supplierNameLabel"])}
                            </Form.Label >
                            <Form.Control 
                                type="text" 
                                value={form.contactName}
                                placeholder={props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["namePlaceholder"])}
                                onChange={(event) => {
                                    const newName = event.target.value
                                    setFormField("contactName",newName)
                                }}
                                isInvalid={!!errors.contactName}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.contactName}
                            </Form.Control.Feedback>
                        </Form.Group>
                        
                        <Form.Group >
                            <Form.Label htmlFor="email"> 
                                {props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["supplierEmailLabel"])}
                            </Form.Label >
                            <Form.Control 
                                type="text" 
                                value={form.contactEmail}
                                placeholder={props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["emailPlaceholder"])}
                                onChange={handleEmailChange}
                                isInvalid={ !!errors.contactEmail }
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.contactEmail}
                            </Form.Control.Feedback>
                            {duplicatedContactsByEmail && getDuplicatedFormatted(duplicatedContactsByEmail)}
                        </Form.Group> 

                        <div className="form-row">
                            <div className="col-12 col-sm-9 p-2">
                                <label htmlFor="primaryPhoneNumber">
                                    <FormattedMessage
                                        id="customer.modal.add_form.primary_phone_number_label"
                                        defaultMessage="Phone Number (Primary)"
                                    />
                                </label>
                                <Form.Control 
                                    type="text" 
                                    id="primaryPhoneNumber"
                                    name="contactPrimaryPhoneNumber"
                                    value={form.contactPrimaryPhoneNumber}
                                    placeholder={props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["primary_phone_number_placeholder"])}
                                    onChange={(event) => {
                                        const contactPrimaryPhoneNumber = event.target.value
                                        setFormField("contactPrimaryPhoneNumber",contactPrimaryPhoneNumber)
                                    }}
                                    isInvalid={ !!errors.contactPrimaryPhoneNumber }
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.contactPrimaryPhoneNumber}
                                </Form.Control.Feedback>
                                {duplicatedContactsByPhoneNumber && getDuplicatedFormatted(duplicatedContactsByPhoneNumber)}
                            </div>

                            <div className="col-12 col-sm-3 pt-2">
                                <label htmlFor="extensionPhoneNumber">
                                    <FormattedMessage
                                        id="customer.modal.add_form.primary_phone_number_extension_label"
                                        defaultMessage="Extension"
                                    />
                                </label>
                                <Form.Control
                                    type="text"
                                    className="form-control"
                                    name="contactPrimaryPhoneNumberExtension"
                                    placeholder="123"
                                    value={form.contactPrimaryPhoneNumberExtension}
                                    onChange={(event) => {
                                        const contactPrimaryPhoneNumberExtension = event.target.value
                                        setFormField("contactPrimaryPhoneNumberExtension",contactPrimaryPhoneNumberExtension)
                                    }}
                                />
                            </div>
                        </div>
                        {showAdvancedForm && <>
                            <div className={"form-row "}>
                                <div className="col-md-12 p-2">
                                    <label htmlFor="mobilePhoneNumber">
                                        <FormattedMessage
                                            id="customer.modal.add_form.mobile_phone_number_label"
                                            defaultMessage="Phone Number (Mobile)"
                                        />
                                    </label>
                                    <Form.Control 
                                        type="text" 
                                        id="mobilePhoneNumber"
                                        name="contactPhoneNumberMobile"
                                        value={form.contactPhoneNumberMobile}
                                        placeholder={props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["mobile_phone_number_placeholder"])}
                                        onChange={(event) => {
                                            const contactPhoneNumberMobile = event.target.value
                                            setFormField("contactPhoneNumberMobile",contactPhoneNumberMobile)
                                        }}
                                        isInvalid={ !!errors.contactPhoneNumberMobile }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.contactPhoneNumberMobile}
                                    </Form.Control.Feedback>
                                </div>
                            </div>

                            <AddressFormFieldsComponent value={form.address} onChange={handleAddressChange} />

                        
                            <Form.Group>
                                <Form.Label htmlFor="language"> 
                                    {props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["languageLabel"])}
                                </Form.Label >
                                <Select
                                    multi={false} 
                                    value={form.contactLanguage}
                                    onChange={(event) => {
                                        if (!event) {
                                            setFormField("contactLanguage","")
                                        } else {
                                            const contactLanguage = event.value
                                            setFormField("contactLanguage",contactLanguage)
                                        }
                                    }}
                                    options={GlobalHelper.getSupportedLocales().map(locale => ({
                                        value: locale,
                                        label: props.intl.formatMessage(GlobalHelper.getLanguageMessage(locale)),
                                    }))}
                                />
                            </Form.Group>
                        
                            <div className="form-row ">
                                <div className="col-md-12 p-2">
                                    <label htmlFor="taxId">
                                        <FormattedMessage
                                            id="supplier.modal.add_form.default_tax_label"
                                            defaultMessage="Default tax"
                                        />
                                    </label>
                                    <TaxSelect
                                        value={form.taxId}
                                        onChange={taxId => {
                                            setFormField("taxId",taxId)
                                        }}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <Form.Group >
                                <Form.Label htmlFor="taxNumber">
                                    {props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["taxNumberLabel"])}
                                </Form.Label >
                                <Form.Control
                                    id="taxNumber"
                                    as="input"
                                    value={form.taxNumber}
                                    onChange={(event) => {
                                        setFormField("taxNumber",event.target.value)
                                    }}
                                />
                            </Form.Group>
                            
                            <Form.Group >
                                <Form.Label htmlFor="notes"> 
                                    {props.intl.formatMessage(CREATE_NEW_SUPPLIER_MESSAGES["notesLabel"])}
                                </Form.Label >
                                <Form.Control 
                                    as="textarea" 
                                    value={form.contactNotes}
                                    onChange={(event) => {
                                        setFormField("contactNotes",event.target.value)
                                    }}
                                />
                            </Form.Group>
                        </>}
                    </Col>
                         
                </div>

                {!showAdvancedForm && <div className="col-12 text-center pb-4">
                    <button className="btn btn-outline-primary btn-sm" onClick={openAdvanced}>
                        <Icon value={"plus"} />
                        &nbsp;
                        <FormattedMessage
                            id="product.form.edit.advanced_btn"
                            defaultMessage="Open advanced form"
                        />
                    </button>
                </div>}
                <Row style={{zIndex:10000} }  className="m-0 py-1 fixed-bottom pr-0 pr-md-5 pr-xl-0">
                    <Col lg={12} xl={11} className="mx-auto pt-3 pl-4 pr-4 pr-md-5 pr-xl-4">
                        <Col className="ui-module sticky-bottom-module">
                            <Row>
                                <Col xs={5} md={6}>
                                    {props.contact?.id && Guard.isAuthorized(Permissions.DeleteContact) &&
                                        <ConfirmButton
                                            onClick={deleteContact}
                                            className="btn btn-danger">
                                            {props.intl.formatMessage(EDIT_STOCK_LOCATION_PAGE_MESSAGES["deleteLabel"])}
                                        </ConfirmButton>
                                    }
                                </Col>
                                <Col xs={7} md={6} className="text-align-right">
                                    <Link to={getRedirectLink()} className="btn btn-light mr-2">
                                        {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["cancelLabel"])}
                                    </Link>
                                    <Button
                                        type="submit"
                                    > 
                                        {props.intl.formatMessage(CREATE_NEW_STOCK_LOCATION_PAGE_MESSAGES["saveLabel"])}
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                    </Col>
                </Row>
            </Form>
        </>
    )

}
export default injectIntl(CreateSupplierForm)



// WEBPACK FOOTER //
// ./src/customer/supplier/components/SupplierForm.tsx